import { useRef, useState } from 'react';
import { Button, Card, DateInput } from '@safetyhub/ui';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { ChevronDownIcon } from '../icons';
import { useModalStore } from '../../stores/modal';
import {
  organizationsApi,
  CreateOrganizationDto,
  Organization,
  OrganizationCategory,
  CATEGORY_OPTIONS,
  extractApiError,
} from '../../lib/organizations';
import Modal from '../Modal';
import { useToastStore } from '../../stores/toast';
import { useCompanyDuplicateModal } from '../../hooks/useCompanyDuplicateModal';
import AddressSearch from '../AddressSearch';
import {
  validateBusinessNumberChecksum,
  normalizeRegistrationNumber,
  formatBusinessNumber,
} from '@safetyhub/shared';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

/**
 * 푸터의 제출 버튼이 본문의 폼을 가리키는 `form` 속성 값. base Modal이 본문과 푸터를 형제
 * div로 렌더하므로 이 연결 없이는 `type="submit"`이 아무 폼에도 속하지 않는다.
 */
const FORM_ID = 'create-organization-form';

// 업종 옵션
const INDUSTRY_OPTIONS = [
  '제조업',
  '건설업',
  '물류/운송업',
  '화학/석유화학',
  '전기/전자',
  '자동차/조선',
  '식품/의약',
  '기타',
];

export default function CreateOrganizationModal() {
  const { createOrganization, closeCreateOrganization } = useModalStore();
  const { fetchUser, setCurrentOrganization } = useAuthStore();
  const { showToast } = useToastStore();
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const enableGuidanceAgency = isEnabled('construction-guidance-agency');
  const visibleCategoryOptions = CATEGORY_OPTIONS.filter(
    (opt) => opt.value !== 'GUIDANCE_AGENCY' || enableGuidanceAgency,
  );

  // 조직 기본 폼 상태
  const [name, setName] = useState('');
  // 열림 시 조직 유형 select에 포커스 — `autoFocus`는 base Modal의 마운트 포커스가 덮는다(리뷰 1103).
  const categorySelectRef = useRef<HTMLSelectElement>(null);
  const [category, setCategory] = useState<OrganizationCategory>('WORKPLACE');
  const [industry, setIndustry] = useState('');

  // 본사 정보 폼 상태
  const [showCompanyFields, setShowCompanyFields] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [companyContact, setCompanyContact] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyZonecode, setCompanyZonecode] = useState('');
  const [companyAddressDetail, setCompanyAddressDetail] = useState('');

  // 특수 조직 검증 필드 상태
  const [agencyBusinessNumber, setAgencyBusinessNumber] = useState('');
  const [agencyOpeningDate, setAgencyOpeningDate] = useState('');
  const [agencyCeoName, setAgencyCeoName] = useState('');
  const [agencyOfficialName, setAgencyOfficialName] = useState('');
  const [agencyAddress, setAgencyAddress] = useState('');
  const [agencyZonecode, setAgencyZonecode] = useState('');
  const [agencyAddressDetail, setAgencyAddressDetail] = useState('');
  const [agencyBusinessNumberError, setAgencyBusinessNumberError] = useState('');

  // 특수 조직 부가 정보 펼침 상태
  const [showAgencyExtra, setShowAgencyExtra] = useState(false);

  // UI 상태
  const [isLoading, setIsLoading] = useState(false);
  const [companyNameTouched, setCompanyNameTouched] = useState(false);

  // 중복 사업자번호 경고 모달 상태
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateOrgs, setDuplicateOrgs] = useState<Array<{
    id: string;
    name: string;
    category: OrganizationCategory;
    createdAt: string;
  }>>([]);

  // FULLY Company 중복 차단 모달 (ORGANIZATION_DUPLICATE_COMPANY)
  const { handleError: handleCompanyDuplicateError, modalNode: companyDuplicateModal } =
    useCompanyDuplicateModal();

  const isSpecialAgency = category === 'GUIDANCE_AGENCY' || category === 'SAFETY_MANAGEMENT_AGENCY';
  // WORKPLACE도 NTS 5필드 검증 필요. WORKSPACE는 사업자 정보 불필요
  const isWorkspace = category === 'WORKSPACE';
  const needsNtsVerification = !isWorkspace && (isSpecialAgency || category === 'WORKPLACE');
  // 검증 대상(WORKPLACE/GUIDANCE_AGENCY/SAFETY_MANAGEMENT_AGENCY)은 상호가 조직명이 되므로 이름 입력란 숨김
  const showNameInput = isWorkspace;

  const handleClose = () => {
    setName('');
    setCategory('WORKPLACE');
    setIndustry('');
    setShowCompanyFields(false);
    setCompanyName('');
    setRegistrationNumber('');
    setCompanyContact('');
    setCompanyEmail('');
    setCompanyAddress('');
    setCompanyZonecode('');
    setCompanyAddressDetail('');
    setCompanyNameTouched(false);
    setAgencyBusinessNumber('');
    setAgencyOpeningDate('');
    setAgencyCeoName('');
    setAgencyOfficialName('');
    setAgencyAddress('');
    setAgencyZonecode('');
    setAgencyAddressDetail('');
    setAgencyBusinessNumberError('');
    setShowAgencyExtra(false);
    setDuplicateModalOpen(false);
    setDuplicateOrgs([]);
    closeCreateOrganization();
  };

  // 사업자등록번호 입력 핸들러 (하이픈 자동 삽입)
  const handleAgencyBusinessNumberChange = (value: string) => {
    const digits = normalizeRegistrationNumber(value).slice(0, 10);
    let formatted = digits;
    if (digits.length > 5) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    } else if (digits.length > 3) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    setAgencyBusinessNumber(formatted);
    setAgencyBusinessNumberError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // WORKSPACE만 이름 입력 필수 (검증 대상은 상호가 조직명)
    if (showNameInput) {
      if (!name.trim()) {
        showToast('조직 이름을 입력해주세요.', 'error');
        return;
      }
      if (name.trim().length < 2) {
        showToast('조직 이름은 2자 이상이어야 합니다.', 'error');
        return;
      }
    }

    // 본사 정보가 펼쳐진 경우 회사명 필수 확인
    if (showCompanyFields && companyName.trim() && companyName.trim().length < 1) {
      showToast('본사 회사명을 입력해주세요.', 'error');
      return;
    }

    // NTS 5필드 검증 (WORKPLACE 포함)
    if (needsNtsVerification) {
      if (!agencyBusinessNumber.trim()) {
        showToast('사업자등록번호를 입력해주세요.', 'error');
        return;
      }
      if (!validateBusinessNumberChecksum(agencyBusinessNumber)) {
        setAgencyBusinessNumberError('유효하지 않은 사업자등록번호입니다.');
        showToast('유효하지 않은 사업자등록번호입니다.', 'error');
        return;
      }
      if (!agencyOpeningDate.trim()) {
        showToast('개업일자를 입력해주세요.', 'error');
        return;
      }
      const dateDigits = agencyOpeningDate.replace(/-/g, '');
      if (!/^\d{8}$/.test(dateDigits)) {
        showToast('유효한 개업일자를 입력해주세요.', 'error');
        return;
      }
      if (!agencyCeoName.trim()) {
        showToast('대표자명을 입력해주세요.', 'error');
        return;
      }
      if (!agencyOfficialName.trim()) {
        showToast('상호를 입력해주세요.', 'error');
        return;
      }
    }

    await submitCreate();
  };

  const buildCreateDto = (confirmDuplicate?: boolean): CreateOrganizationDto => {
    const data: CreateOrganizationDto = {
      category: category,
      // WORKSPACE만 name 전송 (검증 대상은 상호가 조직명이 되므로 생략)
      ...(showNameInput && name.trim() ? { name: name.trim() } : {}),
    };

    if (needsNtsVerification) {
      // NTS 5필드: agency 필드로 검증 정보 전달
      data.agencyBusinessNumber = formatBusinessNumber(normalizeRegistrationNumber(agencyBusinessNumber));
      data.agencyOpeningDate = agencyOpeningDate.replace(/-/g, '');
      data.agencyCeoName = agencyCeoName.trim();
      data.agencyOfficialName = agencyOfficialName.trim();
      data.agencyAddress = agencyAddress.trim() || undefined;
      data.agencyZonecode = agencyZonecode.trim() || undefined;
      data.agencyAddressDetail = agencyAddressDetail.trim() || undefined;

      if (confirmDuplicate) {
        data.confirmDuplicateBusinessNumber = true;
      }

      // 연락처, 이메일, 업종 중 하나라도 있을 때 company 포함 (서버가 나머지 필드를 agency에서 동기화)
      if (companyContact.trim() || companyEmail.trim() || industry) {
        data.company = {
          name: agencyOfficialName.trim(),
          contact: companyContact.trim() || undefined,
          email: companyEmail.trim() || undefined,
          industry: industry || undefined,
        };
      }
    } else if (industry) {
      // 일반 조직 생성 시 industry를 company.industry로 전달
      data.company = { name: name.trim(), industry };
    }

    return data;
  };

  const handleCreateSuccess = async (newOrg: Organization) => {
    await fetchUser();
    setCurrentOrganization(newOrg.id);
    handleClose();

    if (isSpecialAgency) {
      const vs = newOrg.verificationStatus;
      if (vs === 'VERIFIED' || vs === 'NOT_REQUIRED') {
        showToast('조직이 생성되었고 자동 검증이 완료되었습니다.', 'success');
      } else {
        showToast('조직이 생성되었습니다. 지정서를 업로드하여 인증을 완료해주세요.', 'info');
        navigate('/settings/organization');
        return;
      }
    }
    navigate('/');
  };

  const submitCreate = async (confirmDuplicate?: boolean) => {
    setIsLoading(true);
    try {
      const data = buildCreateDto(confirmDuplicate);
      const newOrg = await organizationsApi.create(data);
      await handleCreateSuccess(newOrg);
    } catch (err) {
      if (handleCompanyDuplicateError(err)) {
        // ORGANIZATION_DUPLICATE_COMPANY 처리됨
      } else {
        const apiError = extractApiError(err);
        if (apiError.code === 'ORGANIZATION_DUPLICATE_BUSINESS_NUMBER') {
          const existing = (apiError.details as { existingOrganizations?: Array<{ id: string; name: string; category: OrganizationCategory; createdAt: string }> })?.existingOrganizations ?? [];
          setDuplicateOrgs(existing);
          setDuplicateModalOpen(true);
        } else {
          showToast(apiError.message, 'error');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDuplicate = async () => {
    setDuplicateModalOpen(false);
    await submitCreate(true);
  };

  // TEAM이면 이름 필수, 검증 대상이면 NTS 5필드 필수
  const submitDisabled = isLoading
    || (showNameInput && !name.trim())
    || (needsNtsVerification && (!agencyBusinessNumber || !agencyOpeningDate || !agencyCeoName || !agencyOfficialName || !agencyAddress.trim() || !agencyAddressDetail.trim()));

  return (
    <Modal
      isOpen={createOrganization}
      onClose={handleClose}
      title="새 조직 만들기"
      description="팀과 함께 점검을 관리할 조직을 생성합니다."
      size="md"
      preventClose={isLoading}
      initialFocusRef={categorySelectRef}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            취소
          </Button>
          {/* 푸터는 base가 본문과 **형제**로 렌더하므로 아래 `<form>` 밖이다 — `form` 속성으로
              이어야 제출이 일어난다(없으면 조용히 아무 일도 하지 않는다). */}
          <Button variant="primary" type="submit" form={FORM_ID} disabled={submitDisabled}>
            {isLoading ? '생성 중...' : '조직 만들기'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* 조직 유형 (이름 입력 전 선택하여 입력란 동적 결정) */}
          <div>
            <label htmlFor="org-category" className="block text-sm font-medium text-ink-700">
              조직 유형
            </label>
            <select
              ref={categorySelectRef}
              id="org-category"
              value={category}
              onChange={(e) => {
                const newCategory = e.target.value as OrganizationCategory;
                setCategory(newCategory);
                if (newCategory !== 'WORKPLACE') {
                  setIndustry('');
                }
              }}
              disabled={isLoading}
              className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
            >
              {visibleCategoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-500">
              {category === 'WORKPLACE' && '국세청 사업자등록 정보를 기반으로 인증합니다.'}
              {category === 'GUIDANCE_AGENCY' && '재해예방기술지도기관은 기술지도 관련 기능이 활성화됩니다.'}
              {category === 'SAFETY_MANAGEMENT_AGENCY' && '안전관리전문기관 지정 정보를 기반으로 인증합니다.'}
              {category === 'WORKSPACE' && '사업자 등록 없이 사용하는 개인 또는 팀입니다. 동아리, 스터디, 임시 조직 등에 적합합니다.'}
            </p>
          </div>

          {/* 조직 이름 — WORKSPACE만 표시 (검증 대상은 상호가 조직명) */}
          {showNameInput && (
            <div>
              <label htmlFor="org-name" className="block text-sm font-medium text-ink-700">
                조직 이름 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="org-name"
                value={name}
                onChange={(e) => {
                  const newName = e.target.value;
                  setName(newName);
                  if (!companyNameTouched) {
                    setCompanyName(newName);
                  }
                }}
                disabled={isLoading}
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
                placeholder="예: ABC 팀"
              />
              <p className="mt-1 text-xs text-ink-500">
                팀 멤버를 초대하여 함께 점검을 관리할 수 있습니다.
              </p>
            </div>
          )}


          {/* 업종 - WORKPLACE만 표시 */}
          {category === 'WORKPLACE' && (
            <div>
              <label htmlFor="org-industry" className="block text-sm font-medium text-ink-700">
                업종
              </label>
              <select
                id="org-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                disabled={isLoading}
                className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
              >
                <option value="">선택하세요</option>
                {INDUSTRY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          )}

          {/* NTS 5필드 검증 (GUIDANCE_AGENCY / SAFETY_MANAGEMENT_AGENCY / WORKPLACE) */}
          {needsNtsVerification && (
            <>
              <div className="border border-blue-200 rounded-card p-4 bg-blue-50 space-y-3">
                <p className="text-xs text-blue-700 font-medium">
                  {isSpecialAgency ? '기관 검증 정보' : '사업자 정보'}
                </p>
                <p className="text-xs text-blue-600">
                  {isSpecialAgency
                    ? '등록 후 고용노동부 공시 정보와 대조하여 자동 검증됩니다. 검증 실패 시 수동 심사가 필요합니다.'
                    : '국세청 사업자등록 정보와 일치해야 합니다. 사업자등록증을 참고해 정확히 입력해 주세요.'}
                </p>

                <div>
                  <label htmlFor="agency-business-number" className="block text-sm font-medium text-ink-700">
                    사업자등록번호 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="agency-business-number"
                    value={agencyBusinessNumber}
                    onChange={(e) => handleAgencyBusinessNumberChange(e.target.value)}
                    disabled={isLoading}
                    className={`mt-1 block w-full px-3 py-2 border rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white ${agencyBusinessNumberError ? 'border-red-500' : 'border-ink-300'}`}
                    placeholder="123-45-67890"
                  />
                  {agencyBusinessNumberError && (
                    <p className="mt-1 text-xs text-red-600">{agencyBusinessNumberError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700">
                    개업일자 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mt-1">
                    <DateInput
                      label="개업일자"
                      placeholder="개업일자를 선택하세요"
                      value={agencyOpeningDate}
                      onChange={setAgencyOpeningDate}
                      disabled={isLoading}
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="agency-ceo-name" className="block text-sm font-medium text-ink-700">
                    대표자명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="agency-ceo-name"
                    value={agencyCeoName}
                    onChange={(e) => setAgencyCeoName(e.target.value)}
                    disabled={isLoading}
                    className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                    placeholder="홍길동"
                  />
                </div>

                <div>
                  <label htmlFor="agency-official-name" className="block text-sm font-medium text-ink-700">
                    상호 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="agency-official-name"
                    value={agencyOfficialName}
                    onChange={(e) => {
                      setAgencyOfficialName(e.target.value);
                    }}
                    disabled={isLoading}
                    className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                    placeholder="(주)안전기술원"
                  />
                  <p className="mt-1 text-xs text-ink-500">사업자등록증상 상호</p>
                </div>

                <div>
                  <AddressSearch
                    id="agency-address"
                    address={agencyAddress}
                    addressDetail={agencyAddressDetail}
                    zonecode={agencyZonecode}
                    onChange={(data) => {
                      setAgencyAddress(data.address);
                      setAgencyAddressDetail(data.addressDetail);
                      setAgencyZonecode(data.zonecode);
                    }}
                    label="사업장 주소"
                    disabled={isLoading}
                  />
                </div>

                <p className="text-xs text-blue-600">
                  * 회사명, 사업자등록번호, 주소는 위 정보에서 자동 반영됩니다.
                </p>
              </div>

              {/* 부가 정보 (연락처/이메일) */}
              <div className="border-t pt-4">
                <button
                  type="button"
                  onClick={() => setShowAgencyExtra(!showAgencyExtra)}
                  disabled={isLoading}
                  className="flex items-center gap-1 text-sm font-medium text-ink-700 hover:text-ink-900 disabled:opacity-50"
                >
                  <ChevronDownIcon
                    className={`h-4 w-4 flex-shrink-0 transition-transform ${showAgencyExtra ? '' : '-rotate-90'}`}
                  />
                  <span>부가 정보 (선택)</span>
                </button>
                <p className="mt-1 text-xs text-ink-500">
                  대표 연락처, 이메일을 추가할 수 있습니다.
                </p>

                {showAgencyExtra && (
                  <Card surface="soft" elevation="none" className="mt-3 space-y-3">
                    <div>
                      <label htmlFor="agency-contact" className="block text-sm font-medium text-ink-700">
                        대표 연락처
                      </label>
                      <input
                        type="text"
                        id="agency-contact"
                        value={companyContact}
                        onChange={(e) => setCompanyContact(e.target.value.replace(/[^0-9]/g, ''))}
                        disabled={isLoading}
                        className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                        placeholder="예: 0212345678"
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    </div>

                    <div>
                      <label htmlFor="agency-email" className="block text-sm font-medium text-ink-700">
                        이메일
                      </label>
                      <input
                        type="email"
                        id="agency-email"
                        value={companyEmail}
                        onChange={(e) => setCompanyEmail(e.target.value)}
                        disabled={isLoading}
                        className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                        placeholder="예: contact@agency.com"
                      />
                    </div>
                  </Card>
                )}
              </div>
            </>
          )}

          {/* 본사 정보 섹션 — NTS 검증이 없는 카테고리에서만 표시 (WORKSPACE 제외) */}
          {!needsNtsVerification && !isWorkspace && (
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setShowCompanyFields(!showCompanyFields)}
                disabled={isLoading}
                className="flex items-center gap-1 text-sm font-medium text-ink-700 hover:text-ink-900 disabled:opacity-50"
              >
                <ChevronDownIcon
                  className={`h-4 w-4 flex-shrink-0 transition-transform ${showCompanyFields ? '' : '-rotate-90'}`}
                />
                <span>본사 정보 (선택)</span>
              </button>
              <p className="mt-1 text-xs text-ink-500">
                보고서에 사용됩니다. 나중에 설정에서 입력할 수도 있습니다.
              </p>

              {showCompanyFields && (
                <Card surface="soft" elevation="none" className="mt-3 space-y-3">
                  <div>
                    <label htmlFor="company-name" className="block text-sm font-medium text-ink-700">
                      회사명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company-name"
                      value={companyName}
                      onChange={(e) => {
                        setCompanyName(e.target.value);
                        setCompanyNameTouched(true);
                      }}
                      disabled={isLoading}
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                      placeholder="예: ABC 건설 주식회사"
                    />
                  </div>

                  <div>
                    <label htmlFor="company-reg-number" className="block text-sm font-medium text-ink-700">
                      법인등록번호 / 사업자등록번호
                    </label>
                    <input
                      type="text"
                      id="company-reg-number"
                      value={registrationNumber}
                      onChange={(e) => {
                        setRegistrationNumber(e.target.value);
                      }}
                      disabled={isLoading}
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                      placeholder="예: 123-45-67890"
                    />
                  </div>

                  <div>
                    <label htmlFor="company-contact" className="block text-sm font-medium text-ink-700">
                      대표 연락처
                    </label>
                    <input
                      type="text"
                      id="company-contact"
                      value={companyContact}
                      onChange={(e) => setCompanyContact(e.target.value.replace(/[^0-9]/g, ''))}
                      disabled={isLoading}
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                      placeholder="예: 0212345678"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </div>

                  <div>
                    <label htmlFor="company-email" className="block text-sm font-medium text-ink-700">
                      이메일
                    </label>
                    <input
                      type="email"
                      id="company-email"
                      value={companyEmail}
                      onChange={(e) => setCompanyEmail(e.target.value)}
                      disabled={isLoading}
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100 bg-white"
                      placeholder="예: contact@company.com"
                    />
                  </div>

                  <div>
                    <AddressSearch
                      id="company-address"
                      address={companyAddress}
                      addressDetail={companyAddressDetail}
                      zonecode={companyZonecode}
                      onChange={(data) => {
                        setCompanyAddress(data.address);
                        setCompanyAddressDetail(data.addressDetail);
                        setCompanyZonecode(data.zonecode);
                      }}
                      label="본사 주소"
                      disabled={isLoading}
                    />
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>

      </form>

      {/* 중복 사업자번호 경고 모달 */}
      <DuplicateBusinessNumberModal
        isOpen={duplicateModalOpen}
        existingOrganizations={duplicateOrgs}
        onCancel={() => setDuplicateModalOpen(false)}
        onConfirm={handleConfirmDuplicate}
        isLoading={isLoading}
      />

      {/* FULLY Company 중복 차단 모달 */}
      {companyDuplicateModal}
    </Modal>
  );
}

// ---- 중복 사업자번호 경고 모달 ----

const CATEGORY_LABEL_MAP: Record<OrganizationCategory, string> = {
  WORKPLACE: '일반 사업장',
  GUIDANCE_AGENCY: '건설재해예방기술지도기관',
  SAFETY_MANAGEMENT_AGENCY: '안전관리전문기관',
  WORKSPACE: '개인/팀',
};

interface DuplicateBusinessNumberModalProps {
  isOpen: boolean;
  existingOrganizations: Array<{
    id: string;
    name: string;
    category: OrganizationCategory;
    createdAt: string;
  }>;
  onCancel: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

function DuplicateBusinessNumberModal({
  isOpen,
  existingOrganizations,
  onCancel,
  onConfirm,
  isLoading,
}: DuplicateBusinessNumberModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title="이미 등록된 사업자번호입니다"
      size="md"
      preventClose={isLoading}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            취소
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? '처리 중...' : '지점으로 추가'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          이 사업자등록번호로 이미 다음 조직이 등록되어 있습니다:
        </p>

        <ul className="space-y-2">
          {existingOrganizations.map((org) => (
            <li key={org.id} className="flex items-start gap-3 border border-ink-200 rounded-card-sm px-3 py-2 bg-ink-50">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink-900 truncate">{org.name}</p>
                <p className="text-xs text-ink-500">
                  {CATEGORY_LABEL_MAP[org.category] ?? org.category} · 등록일{' '}
                  {new Date(org.createdAt).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <p className="text-sm text-ink-600 bg-yellow-50 border border-yellow-200 rounded-card p-3">
          다른 지점·지부로 별도 조직을 만드시려면 <strong>지점으로 추가</strong>를 클릭해 주세요.
          사용자 정보가 일치하면 같은 회사 정보를 공유합니다.
        </p>

      </div>
    </Modal>
  );
}
